import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  DISCOVERY_RUNTIME,
  initiateDiscovery,
  nextDiscoveryAnswer,
  prevDiscoveryQuestion,
} from "../../../Services/assessmentV3";
import { getDiscoveryProgress } from "../../../Services/journey";
import { getDomain } from "../../../Services/periodicTable";
import {
  ArchitectureReady,
  SYSTEM_COPY,
  UnlockCelebration,
} from "./Celebration";
import "../style.css";
import { useStandaloneHeader } from "../shared";
import "./style.css";

/**
 * Discovery runner — one component, three in-flow phases:
 *   questions  → v3 Likert loop (initiate → answers/next until isLastResponse)
 *   celebrate  → "You've unlocked …" with the domain's elements
 *   ready      → "Your Personal Behavioral Architecture is Ready"
 *
 * A refresh mid-run simply initiates again: the engine resumes the open
 * attempt server-side (an already-completed attempt answers with the
 * elements payload straight away). Derived discoveries (D5) are not in
 * DISCOVERY_RUNTIME and redirect back to the list — they are never taken.
 */

const LIKERT_ORDER = ["SA", "A", "N", "D", "SD"];

const LIKERT_LABELS = {
  SA: "Strongly Agree",
  A: "Agree",
  N: "Neutral",
  D: "Disagree",
  SD: "Strongly Disagree",
};

/** Normalize an element row from either source — the auto-submit
 * `aris_elements` payload or `getDomain().elements` — into one tile shape. */
const normalizeElement = (el) => {
  if (!el || typeof el !== "object") return null;
  return {
    name: el.name || el.displayName || el.title || el.key || "",
    symbol: el.symbol || "",
    tagline: el.tagline || "",
    level: el.level || el.label || "",
    url: el.element_url || el.icon || el.image || "",
  };
};

const normalizeElements = (list) =>
  (Array.isArray(list) ? list : []).map(normalizeElement).filter(Boolean);

/** The completion payload keys elements by legacy category name; tolerate
 * casing drift the same way DomainCompletionView does. */
const pickElements = (arisElements, categoryName) => {
  if (!arisElements || typeof arisElements !== "object") return [];
  const aliases = [
    categoryName,
    categoryName?.toLowerCase(),
    categoryName === "Judgment" ? "Judgement" : null,
  ].filter(Boolean);
  for (const alias of aliases) {
    if (Array.isArray(arisElements[alias]) && arisElements[alias].length > 0) {
      return normalizeElements(arisElements[alias]);
    }
  }
  return [];
};

const lowerFirst = (text) =>
  text ? text.charAt(0).toLowerCase() + text.slice(1) : "";

function DiscoveryRunnerScreen() {
  const { discoveryKey } = useParams();
  const navigate = useNavigate();
  const runtime = DISCOVERY_RUNTIME[discoveryKey];

  const [phase, setPhase] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);

  // Questions phase.
  const [categoryId, setCategoryId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [jumpToPrev, setJumpToPrev] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Celebration phase.
  const [elements, setElements] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [progressReady, setProgressReady] = useState(false);
  const [domainView, setDomainView] = useState(null);

  useStandaloneHeader();

  const enterQuestions = (payload) => {
    setCategoryId(payload.categoryId);
    setQuestion(payload.currQuestion);
    setTotalQuestions(Number(payload.totalQuestions) || 0);
    setJumpToPrev(Boolean(payload.jump_to_prev));
    setSelectedKey(null);
    setPhase("questions");
  };

  const enterCelebration = (arisElements, categoryName) => {
    setElements(pickElements(arisElements, categoryName));
    setProgressData(null);
    setProgressReady(false);
    setPhase("celebrate");
  };

  // Phase a — initiate (also the resume path after a refresh mid-run).
  useEffect(() => {
    if (!runtime) return undefined;
    let cancelled = false;
    setPhase("loading");
    setQuestion(null);
    setElements([]);
    setProgressData(null);
    setProgressReady(false);
    setDomainView(null);
    (async () => {
      try {
        const payload = await initiateDiscovery(runtime.categoryName);
        if (cancelled) return;
        if (payload?.currQuestion) {
          enterQuestions(payload);
        } else if (payload?.aris_elements || payload?.suggestSubmit) {
          // Attempt already completed server-side — celebrate directly.
          enterCelebration(payload?.aris_elements, runtime.categoryName);
        } else {
          setPhase("error");
        }
      } catch (_) {
        if (!cancelled) setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryKey, reloadKey]);

  // Phase b — once celebrating, load progress (drives the CTA) and, when the
  // completion payload carried no elements (e.g. after a refresh), fall back
  // to the refresh-safe periodic-table read.
  useEffect(() => {
    if (phase !== "celebrate" || !runtime) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const progress = await getDiscoveryProgress();
        if (!cancelled && progress?.resolved !== false) {
          setProgressData(progress);
        }
      } catch (_) {
        /* CTA degrades to "Back to Discoveries" */
      } finally {
        if (!cancelled) setProgressReady(true);
      }
    })();
    (async () => {
      try {
        const view = await getDomain(runtime.domain);
        if (cancelled || !view) return;
        setDomainView(view);
        setElements((current) =>
          current.length > 0 ? current : normalizeElements(view.elements)
        );
      } catch (_) {
        /* tiles keep their placeholder slots */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!runtime) {
    // Unknown or derived discovery (e.g. D5) — it has no runner.
    return <Navigate to="/journey/discoveries" replace />;
  }

  const submitAnswer = async (key) => {
    if (submitting || !question) return;
    setSubmitting(true);
    setSelectedKey(key);
    try {
      const payload = await nextDiscoveryAnswer({
        item_id: categoryId,
        item_type: "category",
        question_id: question.id,
        answer_id: [key],
        skip_question: false,
        ques_arr_limit: 10,
        is_survey: false,
        ui_answer_ids: [key],
        auto_submit: true,
      });
      if (payload?.isLastResponse) {
        enterCelebration(
          payload.aris_elements,
          payload.categoryName || runtime.categoryName
        );
      } else if (payload?.currQuestion) {
        setQuestion(payload.currQuestion);
        setJumpToPrev(Boolean(payload.jump_to_prev));
        setSelectedKey(null);
      } else {
        setPhase("error");
      }
    } catch (_) {
      setSelectedKey(null);
    } finally {
      setSubmitting(false);
    }
  };

  const goPrev = async () => {
    if (submitting || !jumpToPrev || !question) return;
    setSubmitting(true);
    try {
      const payload = await prevDiscoveryQuestion({
        item_id: categoryId,
        item_type: "category",
        curr_question_num: question.number,
        is_survey: false,
      });
      if (payload?.currQuestion) {
        setQuestion(payload.currQuestion);
        setJumpToPrev(Boolean(payload.jump_to_prev));
        setSelectedKey(payload.answer?.[0] || null);
      }
    } catch (_) {
      /* stay on the current question */
    } finally {
      setSubmitting(false);
    }
  };

  // ---- celebration derivations ------------------------------------------------
  const progressItem = progressData?.discoveries?.find(
    (item) => item.discovery_key === discoveryKey
  );
  const celebrationTitle =
    progressItem?.title || domainView?.discovery_title || runtime.categoryName;
  const celebrationSubtitle = progressItem?.description
    ? `Aris now understands more about ${lowerFirst(progressItem.description)}.`
    : "Aris now understands more about you.";

  const nextKey = progressData?.next_discovery_key;
  const nextRunnable = nextKey ? DISCOVERY_RUNTIME[nextKey] : null;
  const foundationDone = Boolean(
    progressData?.all_complete || (nextKey && !nextRunnable)
  );
  const primaryLabel = !progressData
    ? null
    : foundationDone
    ? "Complete Your Foundation"
    : nextRunnable
    ? "Continue to Next Discovery"
    : null;
  const onPrimary = () => {
    if (foundationDone) {
      setPhase("ready");
    } else if (nextRunnable) {
      navigate(`/journey/discoveries/${nextKey}/run`);
    }
  };

  const options = question?.options || {};
  const likertKeys = LIKERT_ORDER.filter((key) => key in options);
  const keys = likertKeys.length > 0 ? likertKeys : LIKERT_ORDER;

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        {phase === "loading" && (
          <div className="journey-card journey-card--status">
            Preparing your discovery…
          </div>
        )}

        {phase === "error" && (
          <div className="journey-card journey-card--status">
            <p className="run-error__text">
              We couldn't load this discovery just now.
            </p>
            <button
              type="button"
              className="journey-button run-error__button"
              onClick={() => setReloadKey((key) => key + 1)}
            >
              Try again
            </button>
            <button
              type="button"
              className="journey-link run-error__back"
              onClick={() => navigate("/journey/discoveries")}
            >
              Back to Discoveries
            </button>
          </div>
        )}

        {phase === "questions" && question && (
          <>
            <div className="journey-header">
              <button
                type="button"
                className="journey-header__back"
                onClick={() => navigate("/journey/discoveries")}
                aria-label="Save and exit"
              >
                ←
              </button>
              <span className="run-progress__count">
                Question {question.number} of {totalQuestions || "—"}
              </span>
            </div>
            <div
              className="journey-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalQuestions}
              aria-valuenow={Math.max(question.number - 1, 0)}
            >
              <span
                className="journey-progress__fill"
                style={{
                  width: totalQuestions
                    ? `${((question.number - 1) / totalQuestions) * 100}%`
                    : "0%",
                }}
              />
            </div>

            <div className="journey-card run-question">
              <p className="run-question__statement">{question.description}</p>
              <div className="run-likert">
                {keys.map((key) => (
                  <button
                    type="button"
                    key={key}
                    className={
                      "run-likert__option" +
                      (selectedKey === key ? " run-likert__option--selected" : "")
                    }
                    disabled={submitting}
                    onClick={() => submitAnswer(key)}
                  >
                    {options[key] || LIKERT_LABELS[key]}
                  </button>
                ))}
              </div>
            </div>

            {jumpToPrev && question.number > 1 && (
              <button
                type="button"
                className="journey-link run-prev"
                onClick={goPrev}
                disabled={submitting}
              >
                ‹ Previous question
              </button>
            )}
          </>
        )}

        {phase === "celebrate" && (
          <UnlockCelebration
            icon={progressItem?.icon}
            title={celebrationTitle}
            subtitle={celebrationSubtitle}
            systemBody={SYSTEM_COPY[runtime.categoryName]}
            elements={elements}
            progressReady={progressReady}
            primaryLabel={primaryLabel}
            onPrimary={onPrimary}
            onBack={() => navigate("/journey/discoveries")}
          />
        )}

        {phase === "ready" && (
          <ArchitectureReady
            finalDiscovery={progressData?.discoveries?.find(
              (item) => item.is_derived || item.status === "EMERGES"
            )}
            onOpen={() => navigate("/elements")}
          />
        )}
      </div>
    </div>
  );
}

export default DiscoveryRunnerScreen;
